import React, { useState } from 'react';
import { Sparkles, Check, Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { toast } from '@clareefai/ui';
import type { Promoter } from '@clareefai/types';
import { useApproveWidgetPromoterMutation } from '../../store/promoterApi';
import ConfirmDialog from '../shared/ConfirmDialog';

const isWidget = (p: Pick<Promoter, 'createdByType'>) => p.createdByType === 'WIDGET';
const isPending = (p: Pick<Promoter, 'approvalStatus'>) => p.approvalStatus === 'PENDING_APPROVAL';

/** "Invited via widget" badge, with a pending-approval state for widget signups awaiting a vendor. */
export const WidgetBadge: React.FC<{ promoter: Pick<Promoter, 'createdByType' | 'approvalStatus'> }> = ({
  promoter,
}) => {
  const { t } = useTranslation();
  if (!isWidget(promoter)) return null;

  if (isPending(promoter)) {
    return (
      <span className="inline-flex items-center space-x-1 px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-700 border border-amber-200">
        <Sparkles className="w-3 h-3" />
        <span>{t('promoters.widget.pendingBadge', 'Widget · Pending approval')}</span>
      </span>
    );
  }
  return (
    <span className="inline-flex items-center space-x-1 px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-700 border border-purple-200">
      <Sparkles className="w-3 h-3" />
      <span>{t('promoters.widget.badge', 'Invited via widget')}</span>
    </span>
  );
};

/**
 * One-click approve for a pending widget promoter. Renders nothing unless the
 * promoter is widget-sourced AND pending. Confirms via a small popup, then flips
 * them to active + reward-eligible.
 */
export const ApproveWidgetAction: React.FC<{
  promoter: Pick<Promoter, 'id' | 'name' | 'createdByType' | 'approvalStatus'>;
}> = ({ promoter }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [approve, { isLoading }] = useApproveWidgetPromoterMutation();

  if (!isWidget(promoter) || !isPending(promoter)) return null;

  const handleConfirm = async () => {
    try {
      await approve(promoter.id).unwrap();
      toast.success(t('promoters.widget.approved', 'Promoter approved'));
      setOpen(false);
    } catch {
      toast.error(t('promoters.widget.approveError', 'Could not approve. Please try again.'));
    }
  };

  return (
    <>
      <button
        onClick={(e) => {
          e.stopPropagation();
          setOpen(true);
        }}
        className="inline-flex items-center space-x-1 px-3 py-1.5 rounded-lg text-xs font-medium text-white bg-amber-600 hover:bg-amber-700 transition-colors"
      >
        <Check className="w-3.5 h-3.5" />
        <span>{t('promoters.widget.approve', 'Approve')}</span>
      </button>
      <ConfirmDialog
        isOpen={open}
        onClose={() => setOpen(false)}
        onConfirm={handleConfirm}
        variant="warning"
        isLoading={isLoading}
        title={t('promoters.widget.approveTitle', 'Approve this promoter?')}
        description={t(
          'promoters.widget.approveDescription',
          `${promoter.name} signed up through your widget. Approving makes them an active promoter and eligible for rewards.`,
        )}
        confirmLabel={t('promoters.widget.approve', 'Approve')}
      />
    </>
  );
};
